import { Box, Text } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { type Duration, hoursToDuration } from 'modules/shift_management/lib/hoursToDuration'
import type { Leave, ShiftsRow } from 'modules/shift_management/lib/types'
import { usePlannedBreaks } from 'modules/time_planning/store/internal/collections/plannedBreaks'

import InformationTooltip from './InformationTooltip'
import useContractDetails from './useContractDetails'

const weekDays: { [key: string]: number } = Object.freeze({
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
})

const contractUnit: { [key: string]: string } = Object.freeze({
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
})

const workableDays = (days: string): number[] => {
  if (!days) return []
  const workables = days?.split(',')
  return workables.reduce((filtered: number[], day) => {
    // As Sunday's value is 0 to match moment.js day of week
    // we can't use !!weekDays[day] as it would return false
    if (weekDays[day] !== undefined) filtered.push(weekDays[day])
    return filtered
  }, [])
}

type ViewUnit = 'daily' | 'weekly' | 'monthly' | 'four-weeks'

const viewTypes: Record<string, ViewUnit> = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  fourWeeks: 'four-weeks',
}

const fullViewType = (viewType: string): string => {
  switch (viewType) {
    case viewTypes.daily:
      return i18n.t('shift_management.hours_information.contract_hours.acronym_day')

    case viewTypes.weekly:
      return i18n.t('shift_management.hours_information.contract_hours.acronym_week')

    case viewTypes.monthly:
      return i18n.t('shift_management.hours_information.contract_hours.acronym_month')

    case viewTypes.fourWeeks:
      return i18n.t('shift_management.hours_information.contract_hours.acronym_four_weeks')

    default:
      return ''
  }
}

const getContractHoursCopy = (value: number, viewType: string): string => {
  if (!value) return i18n.t('shift_management.hours_information.contract_hours.acronym_no_hours')

  const { hours, minutes } = hoursToDuration(value)
  const copy = i18n.t('shift_management.hours_information.contract_hours.new_acronym_hours', {
    hours,
    minutes: minutes || '', // To hide the 0 minutes
  })

  return `${copy}/${fullViewType(viewType)}`
}

const getPlannedHoursCopy = (plannedHours: Duration): string => {
  const { hours, minutes } = plannedHours

  return i18n.t('shift_management.hours_information.planned_hours.new_acronym_hours', {
    hours,
    minutes: minutes || '', // To hide the 0 minutes
  })
}

export interface Props {
  shiftsRow: ShiftsRow
}

/**
 * @deprecated Use HoursDisplay instead, this is not used anymore.
 */
const HoursInformation = ({ shiftsRow }: Props) => {
  const [{ view }] = useSchedule()
  const { viewOptions } = useCurrentViewOptions()
  const {
    employee: { showContractHours, showPlannedHours },
  } = viewOptions

  const { workingHours, frequency, workingWeekDays } = useContractDetails({
    shiftsRow,
  })

  // TODO: Remove this once 'dev_shift_management_graphql_migration' is done to use shift.plannedBreaks
  const plannedBreaksCollection = usePlannedBreaks()
  const canManage = shiftsRow.canManage
  const hasContractHours = showContractHours && canManage

  const viewType = view.viewType
  const plannedHours = shiftsRow.hoursPerRange(view.firstDay, view.lastDay, plannedBreaksCollection)
  const workables = workableDays(workingWeekDays)
  const startDate = moment(view.startAt)
  const endDate = moment(view.endAt)
  const workableWeekDays = workables?.length

  const hoursByDay = (): number => {
    switch (frequency) {
      case contractUnit.day:
        return workingHours

      case contractUnit.month:
        return workingHours / 4.34 / workableWeekDays

      case contractUnit.year:
        return workingHours / 12 / 4.34 / workableWeekDays

      default:
        // week case
        return workingHours / workableWeekDays
    }
  }

  let leavesByHours = 0

  const leaveHoursByDay = (leaves: Leave[]) => {
    let result = 0
    leaves.forEach((leave) => {
      if (leave.hourly) {
        result += leave.hoursAmount
      } else if (leave.halfDay) {
        result += hoursByDay() / 2
      } else {
        result += hoursByDay()
      }
    })
    return result
  }

  const isWorkableDay = (date: Moment) => {
    const dayOfWeek = Number(date.day())
    return workables.includes(dayOfWeek)
  }

  const calculateWorkableDays = (start: Moment, end: Moment) => {
    let days = 0
    const date = start
    while (date <= end) {
      const isWorkable = isWorkableDay(date)
      if (isWorkable) days++
      const dayHasLeave = shiftsRow.leaves.filterByDateApproved(date)
      if (isWorkable && dayHasLeave) leavesByHours += leaveHoursByDay(dayHasLeave)
      date.add(1, 'days')
    }
    return days
  }

  const roundTo = (num: number, precision: number) => {
    const factor = Math.pow(10, precision)
    return Math.round(num * factor) / factor
  }

  const calculateHoursByView = () => {
    const workableDaysByView = calculateWorkableDays(startDate, endDate)

    if (view.isDaily) return !!workableDaysByView ? roundTo(hoursByDay(), 2) : 0

    // For the weekly and monthly view, we don't need to calculate hours
    // based on days of the week if the contract unit matches the view,
    // we can just return the hours from the contract directly.
    const isWeekly = view.isWeekly && frequency === contractUnit.week
    const isMonthly = view.isMonthly && frequency === contractUnit.month

    if (isWeekly || isMonthly) return workingHours

    return roundTo(hoursByDay() * workableDaysByView, 2)
  }

  const hoursByView = calculateHoursByView()

  const calculateHoursWithAbsences = () => {
    return roundTo(hoursByView - leavesByHours, 2)
  }

  const information = []
  if (showPlannedHours) information.push(getPlannedHoursCopy(plannedHours))
  if (hasContractHours) information.push(getContractHoursCopy(hoursByView, viewType))
  if (!information.length) return null

  return (
    <InformationTooltip
      view={view}
      hours={hoursByView}
      absencesHours={leavesByHours != 0 ? calculateHoursWithAbsences() : null}
      unit={frequency}
      days={workingWeekDays}
      plannedHours={plannedHours}
      showPlannedHours={showPlannedHours}
      showContractHours={hasContractHours}
    >
      <Box flexDirection="column">
        <Text size="100" ellipsis>
          {information.join(' - ')}
        </Text>
      </Box>
    </InformationTooltip>
  )
}

export default observer(HoursInformation)
